import { useMemo } from 'preact/hooks'
import { getFilterDisplayCustomisations } from 'libs/searchspring/components/collection/filters/FilterCustomisations'
import { RANGE_INDICATOR } from 'libs/searchspring/components/collection/filters/FilterUtils'

/**
 * Hook to get the fetched filter matching the current filter
 * @param {Object} filter - Current filter configuration
 * @param {Array} fetchedFilters - All fetched filters from the API
 * @returns {Object} The matching fetched filter
 */
export function useFetchedFilter(filter, fetchedFilters) {
  return useMemo(() => {
    if (!filter || !fetchedFilters) return null
    return fetchedFilters.find((current) => current.field === filter.field)
  }, [filter, fetchedFilters])
}

/**
 * Hook to get customizations for a specific filter
 * @param {string} fieldName - The field name to get customizations for
 * @returns {Object} Filter customization object
 */
export function useFilterCustomizations(fieldName) {
  return useMemo(() => {
    if (!fieldName) return {}
    return getFilterDisplayCustomisations(fieldName)
  }, [fieldName])
}

/**
 * Hook to transform filter values with all needed properties
 * @param {Object} filter - The filter configuration
 * @param {Set} activeFilters - Set of active filter values
 * @param {Object} fetchedFilter - The matching fetched filter
 * @param {Object} customizations - Filter customizations
 * @returns {Array} Transformed filter values
 */
export function useFilterValues(filter, activeFilters, fetchedFilter, customizations) {
  return useMemo(() => {
    if (!filter?.values) return []

    return filter.values.map((item) => {
      const value = item.type === 'range' ? `${item.low}${RANGE_INDICATOR}${item.high}` : item.value
      const valueWithKey = `${filter.field}:${value}`

      const fetchedValue = fetchedFilter?.values?.find((currentValue) => {
        return currentValue.label === item.label
      })

      // Find the matching customization value for this filter item
      const customValue = customizations.values?.find(
        (customItem) => customItem.value === item.label
      ) || {}

      const {
        icon = false,
        icon_id: iconID = false,
        icon_aspect_ratio: aspectRatio = false,
        icon_width: iconWidth = false,
        filter_colour: filterColour = false
      } = customValue

      return {
        label: item.label,
        value: valueWithKey,
        count: fetchedValue?.count,
        checked: activeFilters?.has(valueWithKey),
        disabled: !fetchedValue,
        color: filterColour,
        icon: (icon && iconID && aspectRatio) ? { icon, iconID, aspectRatio, iconWidth } : false
      }
    })
  }, [filter, activeFilters, fetchedFilter, customizations])
}

/**
 * Hook to format active filter labels based on filter type and configuration
 * @param {Object} filter - The filter configuration
 * @param {Array} values - Processed filter values
 * @returns {String|Number} Formatted filter label or count
 */
export function useActiveFilterLabel(filter, values, fetchedFilter) {
  return useMemo(() => {
    if (!filter) return 0

    // Handle slider type filters
    if (filter.type === 'slider') {
      // Check if active values exist
      if (fetchedFilter.facet_active && fetchedFilter.active && Array.isArray(fetchedFilter.active) && fetchedFilter.active.length >= 2) {
        // Use format string if available
        if (filter.format) {
          return formatRange(fetchedFilter.active, filter.format)
        }
        // Fallback to simple format
        return `${fetchedFilter.active[0]} - ${fetchedFilter.active[1]}`
      }
      return 0
    }

    // For non-slider filters, count checked items
    return values?.filter((item) => item.checked)?.length || 0
  }, [filter, values, filter.facet_active])
}

// Dynamically format the range values using the provided format string
export function formatRange(range, formatStr) {
  let index = 0
  // This regex matches placeholders like %01.2f or variations thereof
  return formatStr.replace(/%0?(\d*)\.(\d+)f/g, (match, width, precision) => {
    const num = range[index++]
    // Apply formatting using the specified precision
    return parseFloat(num).toFixed(precision)
  })
}

export function formatValue(value, formatStr) {
  const formatParts = formatStr.split(/\s.*-\s.*/)
  if (!formatParts.length) {
    return value
  }
  // Replace only the first occurrence of a placeholder
  return formatParts[0].replace(/%0?(\d*)\.(\d+)f/, (match, width, precision) => {
    return parseFloat(value).toFixed(precision)
  })
}
